import { message } from 'antd';
import { useState } from 'react';
import ActionColumnComponent from 'src/app/components/ActionColumnComponent/ActionColumnComponent';
import PDFViewer from 'src/app/components/PDFViewer/PDFViewer';
import showModal from 'src/app/components/ShowModalComponent/ShowModal';
import TableComponent, { CustomColumnsType } from 'src/app/components/TableComponent/TableComponent';
import useGetTableData from 'src/app/hooks/useGetTableData';
import useVirtualizedTable from 'src/app/hooks/useVirtualizedTable';
import { generateStudentReportPdf } from 'src/app/pages/StudentsPage/Reports/reports.config';
import { convertStudentReportDataToTableData } from 'src/app/pages/StudentsPage/students-page.config';
import {
  useDeleteGeneralReportMutation,
  useDownloadGeneralReportMutation,
  useGetGeneralReportsInfiniteQuery,
} from 'src/app/redux/generalReports/general-reports.slice';
import { GeneralReportType } from 'src/enums/general-report.enum';
import { StudentReportColumnType } from 'src/types/table.type';

const TABLE_HEIGHT = 'calc(100vh - 20rem)';
const Reports = () => {
  const [lastClickedId, setLastClickedId] = useState('');

  const { data: reportList, vt } = useVirtualizedTable({
    queryFn: useGetGeneralReportsInfiniteQuery,
    args: { types: [GeneralReportType.Student] },
    tableHeight: TABLE_HEIGHT,
  });

  const { tableDataSource } = useGetTableData({
    convertFn: convertStudentReportDataToTableData,
    data: reportList,
  });
  const [downloadReport] = useDownloadGeneralReportMutation();
  const [deleteReport] = useDeleteGeneralReportMutation();
  const onClickReportDownload = async (report: StudentReportColumnType) => {
    const reportData = await downloadReport({ report_id: report.id }).unwrap();
    const doc = await generateStudentReportPdf(reportData);
    if (doc) doc.save(`${report.name}.pdf`);
  };

  const onShowReport = async (report: StudentReportColumnType) => {
    const reportData = await downloadReport({ report_id: report.id }).unwrap();
    const doc = generateStudentReportPdf(reportData);
    // const doc = generateStudentReportPdf(testReport);
    if (!doc) return;
    const blob = new Blob([doc.output('arraybuffer')], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    showModal({
      title: `${report.name}.pdf`,
      content: <PDFViewer file={url} />,
      size: 'xlarge-2',
      hasFooter: false,
      backgroundColor: 'white',
    });
  };

  const onReportDelete = (event: any, reportId: string) => {
    showModal({
      event,
      title: 'Delete Report',
      content: 'Are you sure you want to delete report ?',
      onSave: (modal) => {
        deleteReport(reportId)
          .then(() => message.success('Report deleted successfully'))
          .catch(() => message.error('Report can not be deleted'));
        modal.destroy();
      },
      okText: 'Delete',
    });
  };
  const columns: CustomColumnsType<StudentReportColumnType> = [
    {
      title: 'Name',
      dataIndex: 'name',
    },
    { title: 'Last Created Date', dataIndex: 'lastCreatedDate' },
    {
      title: 'Period',
      dataIndex: 'period',
      render: (value) => {
        return <div style={{ textTransform: 'capitalize' }}>{value}</div>;
      },
    },
    {
      width: '10%',
      title: '',
      fixed: 'right',
      render: (_val, record) => {
        return (
          <ActionColumnComponent
            showComponent={{
              onClick: () => {
                setLastClickedId(record.id);
                onShowReport(record);
              },
              title: !record.hasReport ? 'Report is not ready' : 'View',
              disabled: !record.hasReport,
            }}
            downloadComponent={{
              onClick: () => {
                if (!record.hasReport) return;
                onClickReportDownload(record);
              },
              disabled: !record.hasReport,
              title: !record.hasReport ? 'Report is not ready' : 'Download',
            }}
            deleteComponent={{
              onClick: (e) => {
                onReportDelete(e, record.id);
              },
              title: 'Delete',
            }}
          />
        );
      },
    },
  ];

  return (
    <TableComponent
      lastClickedId={lastClickedId}
      components={vt}
      columns={columns}
      dataSource={tableDataSource}
      scroll={{ y: TABLE_HEIGHT }}
    />
  );
};

export default Reports;
